import type { Countries } from '@/entities/public/countries';
import { AuthApiS, AuthConstantS, AuthErrorS, type AuthTypeS, AuthValidationS } from '@/features/Auth/outbound/share';
import { SnsErrorS } from '@/features/Sns/outbound/share';
import { MCO_DOMAIN } from '@/libs/constant';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { T } from '@/libs/translation';
import { formatMobile, removeFormatMobile } from '@/libs/util/formatMobile';
import { isFormModified } from '@/libs/util/isFormModified';
import { makeNestedFormDataToObject } from '@/libs/util/makeNestedFormDataToObject';
import { makeResultUrl } from '@/libs/util/makeServiceUrl';
import { postUserDataChagedMessage } from '@/libs/util/openerPostMessage';
import { removeSearchParams } from '@/libs/util/removeSearchParams';
import { resizeS3Image } from '@/libs/util/resizeS3Image';
import { trimToNumber } from '@/libs/util/trimToNumber';
import { checkOnlyValidNicknameChar, checkValidWordNicknameChar } from '@/libs/validation/nickname';
import {
  Button,
  Dropdown,
  DropdownChangeEvent,
  type DropdownItemData,
  Input,
  InputChangeEvent,
  type InputValidatorOption,
  Label,
  Link,
  Radio,
  Typo,
} from '@/ui/component/atom';
import { Header, Layout, Modal, Toast } from '@/ui/component/cell';
import {
  ChangeEmailEvent,
  ChangePasswordEvent,
  ChangeProfileImageEvent,
  SnsDisconnectEvent,
} from '@/ui/component/event';
import { ChangeEmailModalBodyView, ProfileImage, SetPasswordView, SnsList } from '@/ui/component/view';
import { getSharedData, type SharedData, PostMessage } from '@/ui/util';
import { delay, each, entries, filter, map, pipe, reject, toArray, values } from '@fxts/core';
import { mauth } from '@marpple/auth';
import { isAxiosError } from 'axios';
import { html, type Html, Page, rune } from 'rune-ts';
import { ZodError } from 'zod';
import { LogoView, makeErrorPopup, makePopup } from '@/ui/component';
import klass from './MyPagePage.module.scss';

export interface MyPagePageData {
  user: AuthTypeS.UserProfileType & { hasPassword?: boolean };
  open?: 'true';
  error?: string;
  connected?: string;
  redirect_url?: string;
  countries: Countries[];
}

export class MyPagePage extends Page<MyPagePageData> {
  component: {
    email: Input;
    nickname: Input;
    name: Input;
    post_code: Input;
    address: Input;
    address_detail: Input;
    mobile: Input;
    birthday: Input;
    set_password_view: SetPasswordView;
    change_email_button: Button;
    search_postal_code_button: Button;
    cancel_button: Button;
    submit_button: Button;
    withdrawal_link: Link;
    header: Header;
    sns_list: SnsList;
    country: Dropdown;
    street: Input;
    detail: Input;
    city: Input;
    state: Input;
    postal_code: Input;
  };

  private profile_image?: string | ArrayBuffer;

  constructor(data: MyPagePageData, sharedData: SharedData) {
    super(data, sharedData);
    const { lang, client_id } = sharedData;
    const { email, nickname, name, mobile, birthday } = data.user;
    const user_address = this.data.user.$one.user_address;
    const user_abroad_address = this.data.user.$one.user_abroad_address;

    const countries =
      lang === 'kr'
        ? []
        : (pipe(
            this.data.countries,
            map(({ name, dialing_code }) => ({
              label: name,
              value: dialing_code,
              checked: user_abroad_address?.country === name,
            })),
            toArray,
          ) as DropdownItemData[]);

    this.component = {
      email: new Input(
        { value: email, id: 'email' },
        {
          attr: {
            name: 'email',
            type: 'email',
            inputmode: 'email',
            placeholder: T('mypage.placeholder.001'),
            readonly: true,
          },
        },
      ),
      nickname: new Input(
        { value: nickname, id: 'nickname' },
        {
          delete_pattern: /\s+/g,
          attr: { type: 'text', name: 'nickname', placeholder: T('mypage.placeholder.002') },
          helper: {
            validators: this.nicknameValidOption(),
          },
        },
      ),
      name: new Input(
        { value: name ?? '', id: 'name' },
        { attr: { type: 'text', name: 'name', placeholder: T('mypage.placeholder.003'), maxlength: 30 } },
      ),
      post_code: new Input(
        { value: user_address?.postcode ?? '' },
        {
          attr: {
            type: 'text',
            name: 'user_address.postcode',
            readonly: true,
          },
        },
      ),
      address: new Input(
        { value: user_address?.address ?? '' },
        {
          attr: {
            type: 'text',
            name: 'user_address.address',
            readonly: true,
          },
        },
      ),
      address_detail: new Input(
        { value: user_address?.address_detail ?? '', id: 'address_detail' },
        {
          attr: {
            type: 'text',
            name: 'user_address.address_detail',
            placeholder: T('mypage.placeholder.007'),
          },
        },
      ),
      mobile: new Input(
        { value: mobile ? formatMobile(mobile) : '', id: 'mobile' },
        { attr: { type: 'text', name: 'mobile', placeholder: T('mypage.placeholder.005'), maxlength: 12 } },
      ),
      birthday: new Input(
        { value: birthday ? birthday.replaceAll(/[^0-9]/g, '') : '', id: 'birthday' },
        {
          attr: {
            type: 'text',
            name: 'birthday',
            placeholder: T('mypage.placeholder.006'),
            maxlength: 8,
            inputmode: 'numeric',
          },
        },
      ),
      set_password_view: new SetPasswordView({ email: data.user.email, hasPassword: data.user.hasPassword }),
      change_email_button: new Button(
        {
          label: new Typo({ text: T('mypage.button.003') }, { typo: 'p2', weight: 'bold' }),
        },
        { type: 'button', size: 'small', color: 'primary' },
      ),
      search_postal_code_button: new Button(
        {
          label: new Typo({ text: T('mypage.button.005') }, { typo: 'p2', weight: 'bold' }),
        },
        { type: 'button', size: 'small', color: 'secondary' },
      ),
      cancel_button: new Button(
        { label: new Typo({ text: T('mypage.button.006') }) },
        { type: 'button', color: 'line', size: 'large' },
      ),
      submit_button: new Button(
        { label: new Typo({ text: T('mypage.button.007') }) },
        { type: 'submit', size: 'large', disabled: true },
      ),
      withdrawal_link: new Link({
        label: new Typo({ text: T('mypage.label.012') }, { typo: 'p2', weight: 'bold', underline: true }),
        href: `/${lang}/withdraw?client_id=${client_id}&open=${data.open ?? false}`,
      }),
      header: new Header({ title: new Typo({ text: T('mypage.title') }, { typo: 'h3', weight: 'bold' }) }),
      sns_list: new SnsList({ user: this.data.user }),
      country: new Dropdown({
        placeholder: T('mypage.label.select_country'),
        has_empty_option: true,
        name: 'user_abroad_address.country',
        items: countries,
      }),
      street: new Input(
        { value: user_abroad_address.street ?? '', id: 'street' },
        {
          attr: {
            type: 'text',
            name: 'user_abroad_address.street',
            placeholder: T('mypage.placeholder.street'),
          },
        },
      ),
      detail: new Input(
        { value: user_abroad_address.detail ?? '', id: 'detail' },
        {
          attr: {
            type: 'text',
            name: 'user_abroad_address.detail',
            placeholder: T('mypage.placeholder.detail'),
          },
        },
      ),
      city: new Input(
        { value: user_abroad_address.city ?? '', id: 'city' },
        {
          attr: {
            type: 'text',
            name: 'user_abroad_address.city',
            placeholder: T('mypage.placeholder.city'),
          },
        },
      ),
      state: new Input(
        { value: user_abroad_address.state ?? '', id: 'state' },
        {
          attr: {
            type: 'text',
            name: 'user_abroad_address.state',
            placeholder: T('mypage.placeholder.state'),
          },
        },
      ),
      postal_code: new Input(
        { value: user_abroad_address.postcode ?? '', id: 'postal_code' },
        {
          attr: {
            type: 'text',
            name: 'user_abroad_address.postcode',
            placeholder: T('mypage.placeholder.postal_code'),
          },
        },
      ),
    };
  }

  nicknameValidOption(): InputValidatorOption[] {
    return [
      {
        message: T('mypage.validation.nickname_valid_1'),
        validator: (val) => val.length >= 2 && val.length <= 10,
      },
      {
        message: T('mypage.validation.nickname_valid_2'),
        validator: checkOnlyValidNicknameChar,
      },
      {
        message: T('mypage.validation.nickname_valid_3'),
        validator: checkValidWordNicknameChar,
      },
    ];
  }

  protected override template(): Html {
    const { device, theme, lang } = getSharedData(this);
    const { name, profile_image, gender, is_agree_sms_notification, is_agree_email_notification } = this.data.user;

    const _profile_image =
      this.profile_image ??
      (profile_image?.includes('s3.marpple.co')
        ? resizeS3Image({ url: profile_image, width: 320, format: 'avif' })
        : profile_image);

    return html`<div>
      ${new Layout(
        {
          view: html`<div class="${klass.page_wrapper}">
            <div class="${klass.mypage}">
              ${device === 'pc'
                ? html`<div class="${klass.title}">
                    ${new LogoView({ theme })} ${new Typo({ text: T('mypage.title') })}
                  </div>`
                : ''}
              <form class="${klass.form}">
                <div class="${klass.profile_image}">
                  ${new ProfileImage({ profile_image: _profile_image, name })}
                  <div class="${klass.profile_image_description}">
                    ${new Typo(
                      {
                        text: html`${T('mypage.description.profile.001')}<br />${T('mypage.description.profile.002')}`,
                      },
                      { typo: 'p2' },
                    )}
                  </div>
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.001') })}</div>
                  ${this.component.sns_list}
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.002') }, { required: true, for_id: 'email' })}</div>
                  <div class="${klass.input_group_button}">
                    ${this.component.email} ${this.component.change_email_button}
                  </div>
                  <div class="${klass.field_description}">
                    ${new Typo({ text: T('mypage.description.email.001') }, { typo: 'p3' })}
                  </div>
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.003') })}</div>
                  ${this.component.set_password_view}
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.004') }, { for_id: 'nickname' })}</div>
                  <div>${this.component.nickname}</div>
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.005') }, { for_id: 'name' })}</div>
                  <div>${this.component.name}</div>
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.007') }, { for_id: 'mobile' })}</div>
                  <div>${this.component.mobile}</div>
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.006') }, { for_id: 'address_detail' })}</div>
                  <div class="${klass.address_wrapper}">
                    ${lang === 'kr'
                      ? html`<div class="${klass.input_group_button}">
                            ${this.component.post_code} ${this.component.search_postal_code_button}
                          </div>
                          <div class="${klass.address}">${this.component.address}</div>
                          <div class="${klass.address}">${this.component.address_detail}</div>
                          <div class="${klass.address_layer}">
                            <img
                              src="//t1.daumcdn.net/postcode/resource/images/close.png"
                              style="cursor:pointer;position:absolute;right:0;top:-1px;z-index:1"
                              alt="${T('mypage.label.013')}"
                            />
                          </div>`
                      : html`${this.component.country}${lang === 'en'
                          ? html`${this.component.street}${this.component.detail}${this.component.city}${this.component
                              .state}${this.component.postal_code}`
                          : html`${this.component.postal_code}${this.component.state}${this.component.city}${this
                              .component.street}${this.component.detail}`}`}
                  </div>
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.008') }, { for_id: 'birthday' })}</div>
                  <div>${this.component.birthday}</div>
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.009') })}</div>
                  <div class="${klass.input_group_radio}">
                    ${pipe(
                      AuthConstantS.USER_GENDER_MAPPER,
                      values,
                      map((_gender) => {
                        return html`${new Radio({
                          name: 'gender',
                          value: _gender,
                          label: new Typo({ text: AuthConstantS.USER_GENDER_NAME_MAPPER[_gender] }),
                          active: gender === _gender,
                        })}`;
                      }),
                      toArray,
                    )}
                  </div>
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.010') })}</div>
                  <div class="${klass.input_group_radio}">
                    ${new Radio({
                      name: 'is_agree_sms_notification',
                      value: 'true',
                      label: new Typo({ text: T('mypage.agreement') }),
                      active: is_agree_sms_notification,
                    })}
                    ${new Radio({
                      name: 'is_agree_sms_notification',
                      value: 'false',
                      label: new Typo({ text: T('mypage.disagreement') }),
                      active: !is_agree_sms_notification,
                    })}
                  </div>
                </div>
                <div class="${klass.field}">
                  <div>${new Label({ label: T('mypage.label.011') })}</div>
                  <div class="${klass.input_group_radio}">
                    ${new Radio({
                      name: 'is_agree_email_notification',
                      value: 'true',
                      label: new Typo({ text: T('mypage.agreement') }),
                      active: is_agree_email_notification,
                    })}
                    ${new Radio({
                      name: 'is_agree_email_notification',
                      value: 'false',
                      label: new Typo({ text: T('mypage.disagreement') }),
                      active: !is_agree_email_notification,
                    })}
                  </div>
                </div>
                <div class="${klass.field}">
                  <div class="${klass.button_group}">
                    ${this.component.cancel_button} ${this.component.submit_button}
                  </div>
                </div>
                <div class="${klass.field}">
                  <div class="${klass.withdrawal}">${this.component.withdrawal_link}</div>
                </div>
              </form>
            </div>
          </div>`,
        },
        device === 'mo' ? { header: this.component.header } : {},
      )}
    </div>`;
  }

  private deactivateSubmitButton() {
    this.component.submit_button.option.disabled = true;
    this.component.submit_button.redraw();
    PostMessage.myPageClean();
  }

  private activateSubmitButton() {
    this.component.submit_button.option.disabled = false;
    this.component.submit_button.redraw();
    PostMessage.myPageDirty();
  }

  private async cancel() {
    const { theme } = getSharedData(this);

    if (!this.component.submit_button.option.disabled) {
      const res = await makePopup({
        title: T('mypage.popup.title.002'),
        message: T('mypage.popup.message.002', { service: T(`common.service_${theme}`, { context: 'suffix' }) }),
        ok: T('mypage.button.010'),
        cancel: T('common.cancel'),
      });

      if (res === 'confirm') {
        return this.returnToService();
      }
    } else {
      return this.returnToService();
    }
  }

  private async returnToService() {
    const { theme, is_app, client_id } = getSharedData(this);
    if (
      (!this.data.redirect_url || this.data.redirect_url === MCO_DOMAIN[getSharedData(this).theme]) &&
      is_app &&
      theme === 'MarppleShop'
    ) {
      PostMessage.resetMypage();
    } else {
      await delay(100);

      if (this.data.open === 'true') {
        return window.close();
      }

      return location.replace(makeResultUrl(client_id));
    }
  }

  get NEED_CHECK_MODIFIED_FIELDS() {
    return [
      'nickname',
      'name',
      'mobile',
      'birthday',
      'gender',
      'is_agree_sms_notification',
      'is_agree_email_notification',
      'user_address.postcode',
      'user_address.address',
      'user_address.address_detail',
      'user_abroad_address.dialing_code',
      'user_abroad_address.postcode',
      'user_abroad_address.detail',
      'user_abroad_address.street',
      'user_abroad_address.city',
      'user_abroad_address.state',
    ];
  }

  private canSubmitForm(name: string) {
    if (this.NEED_CHECK_MODIFIED_FIELDS.includes(name)) {
      const initial = {
        ...this.data.user,
        'user_address.postcode': this.data.user.$one.user_address.postcode ?? '',
        'user_address.address': this.data.user.$one.user_address.address ?? '',
        'user_address.address_detail':
          getSharedData(this).lang === 'kr' &&
          this.component.address.value ===
            '' /* 상세 주소의 경우, 상세 주소만 저장할 수 없으므로 변경사항 검증에서 제외하기 위함 */
            ? this.component.address_detail.value
            : (this.data.user.$one.user_address.address_detail ?? ''),
        'user_abroad_address.postcode': this.data.user.$one.user_abroad_address.postcode,
        'user_abroad_address.detail': this.data.user.$one.user_abroad_address.detail,
        'user_abroad_address.street': this.data.user.$one.user_abroad_address.street,
        'user_abroad_address.city': this.data.user.$one.user_abroad_address.city,
        'user_abroad_address.state': this.data.user.$one.user_abroad_address.state,
      };

      pipe(
        initial,
        entries,
        each(([key, value]) => {
          if (value === null) {
            initial[key] = '';
          }
        }),
      );

      if (
        isFormModified({
          parent: this.element(),
          names: this.NEED_CHECK_MODIFIED_FIELDS,
          initial,
          hook: { mobile: removeFormatMobile, birthday: (birthday: string) => birthday?.replaceAll(/[^0-9]/g, '') },
        }) &&
        this.component.nickname.isValid()
      ) {
        this.activateSubmitButton();
      } else {
        this.deactivateSubmitButton();
      }
    }
  }

  private async errorCheck() {
    const { error } = this.data;

    if (error) {
      if (error === SnsErrorS.SNS_004.name) {
        await makeErrorPopup(error, SnsErrorS.SNS_004().json);
      }

      if (error === SnsErrorS.SNS_006.name) {
        await makeErrorPopup(error, SnsErrorS.SNS_006().json);
      }

      removeSearchParams({ name: 'error' });
    }
  }

  override onRender() {
    const { connected } = this.data;

    this.errorCheck().catch(console.error);

    if (connected === 'true') {
      Toast.open({ label: T('mypage.toast.message.004'), close_label: T('common.close') }, {});
      removeSearchParams({ name: 'connected' });
    }

    this.component.birthday.addEventListener('input', trimToNumber);
    this.component.mobile.addEventListener('input', trimToNumber);
    this.component.mobile.addEventListener('focusout', () => {
      this.component.mobile.value = formatMobile(this.component.mobile.value);
    });
    this.component.mobile.addEventListener('focusin', () => {
      this.component.mobile.value = removeFormatMobile(this.component.mobile.value);
    });

    // input 변경사항 확인
    this.delegate('input', 'input[type="radio"]', (e) => {
      const input = e.target as HTMLInputElement;
      const form = (e.target as HTMLDivElement).closest(`.${klass.form}`);
      if (form) {
        this.canSubmitForm(input.name);
      }
    });

    this.delegate(InputChangeEvent, Input, (e, input) => {
      const form = (e.target as HTMLDivElement).closest(`.${klass.form}`);
      if (form) {
        this.canSubmitForm(input.option.attr?.name || '');
      }
    });

    this.delegate(DropdownChangeEvent, Dropdown, ({ detail: { selected } }) => {
      const saved_country = this.data.user.$one.user_abroad_address.country;
      if (saved_country !== selected.label) {
        this.activateSubmitButton();
      } else {
        this.deactivateSubmitButton();
      }
    });

    // 마이페이지 저장
    this.delegate('submit', `.${klass.form}`, async (e) => {
      try {
        e.originalEvent.preventDefault();

        const form = e.currentTarget as HTMLFormElement;

        const data = pipe(
          form,
          makeNestedFormDataToObject,
          (obj: Record<PropertyKey, any>) => {
            // form field 중 boolean이 string으로 들어가 있는 경우 처리
            pipe(
              obj,
              entries,
              filter(([_, value]) => value === 'true' || value === 'false'),
              each(([key, value]) => {
                obj[key] = value === 'true';
              }),
            );
            return obj;
          },
          (obj: AuthTypeS.ProfileChangeControllerReqBodyType) => {
            const user_address = obj?.user_address?.postcode ? { ...obj?.user_address } : {};
            const user_abroad_address =
              getSharedData(this).lang !== 'kr'
                ? {
                    ...obj.user_abroad_address,
                    dialing_code: this.component.country.value,
                    country:
                      this.component.country.displayName === this.component.country.data.placeholder
                        ? ''
                        : this.component.country.displayName,
                  }
                : {};

            return {
              ...obj,
              mobile: obj.mobile ? removeFormatMobile(obj.mobile) : obj.mobile,
              ...user_address,
              ...{ user_abroad_address },
              ...{ birthday: obj?.birthday === '' ? null : obj?.birthday },
            };
          },
          AuthValidationS.validateChangeProfileReqBody,
        );

        const res = await AuthApiS.postChangeProfile(data);
        await mauth.refreshUser();
        this.data.user = { ...res.data, hasPassword: this.data.user.hasPassword };
        this.component.nickname.value = res.data.nickname;
        this.component.nickname.resetValidator();

        this.deactivateSubmitButton();

        const { theme } = getSharedData(this);
        const result = await makePopup({
          title: T('mypage.popup.title.001'),
          message: T('mypage.popup.message.001', { service: T(`common.service_${theme}`, { context: 'suffix' }) }),
          ok: T('mypage.button.010'),
          cancel: T('mypage.button.006'),
        });

        if (result === 'confirm') {
          postUserDataChagedMessage();
          return this.returnToService();
        }
      } catch (e) {
        if (isFeatureError(e)) {
          if (e.json.code === AuthErrorS.USER_002.name) {
            if (e.cause instanceof ZodError) {
              const issues = e.cause.issues;
              const too_small_issues = pipe(
                issues,
                filter(({ code }) => code === 'too_small'),
              );
              const birthday_issue = pipe(
                too_small_issues,
                filter(({ path }) => path.includes('birthday')),
                toArray,
              );

              if (birthday_issue.length > 0) {
                await makeErrorPopup(e, {
                  title: T('mypage.validation.birthday_title'),
                  message: T('mypage.validation.birthday_description'),
                });
                this.component.birthday.focus();
                return;
              }

              return makeErrorPopup(e, e.json);
            }
          }
          return makeErrorPopup(e, e.json);
        }

        if (isAxiosError(e)) {
          return makeErrorPopup(e, e?.response?.data);
        }

        return makeErrorPopup(e);
      }
    });

    // 프로필 이미지 수정
    this.addEventListener(ChangeProfileImageEvent, (e) => {
      this.data.user.profile_image = e.detail.file_url;
    });

    // 우편번호 검색
    this.component.search_postal_code_button.addEventListener('click', (_e) => {
      const current_scroll = Math.max(document.body.scrollTop, document.documentElement.scrollTop);

      const form = this.element().querySelector('form')!;
      const address_wrapper_els = form.querySelectorAll(`.${klass.address}`);

      const address_layer = this.element().querySelector(`.${klass.address_layer}`)!;
      if (!address_layer) {
        return;
      }

      new (window as any).daum.Postcode({
        oncomplete: (data) => {
          let fullAddr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
          let extraAddr = '';
          if (data.userSelectedType === 'R') {
            if (data.bname !== '') extraAddr += data.bname;
            if (data.buildingName !== '') extraAddr += extraAddr !== '' ? ', ' + data.buildingName : data.buildingName;
            fullAddr += extraAddr !== '' ? ' (' + extraAddr + ')' : '';
          }

          this.component.post_code.inputBaseView.setValue(data.zonecode);
          this.component.address.inputBaseView.setValue(fullAddr);

          pipe(
            address_wrapper_els,
            each((el) => el.classList.add(klass.visible)),
          );

          const event = new Event('input', { bubbles: true });
          this.component.post_code.inputBaseView.dispatchEvent(event);

          this.component.address_detail.focus();

          (address_layer as HTMLDivElement).style.display = 'none';

          document.body.scrollTop = current_scroll;
        },
        width: '100%',
        height: '100%',
      }).embed(address_layer);

      (address_layer as HTMLDivElement).style.display = 'block';

      window.scrollTo({
        top: (address_layer as HTMLDivElement).offsetTop - 64,
        behavior: 'smooth',
      });
    });

    // 우편번호 검색 layer 닫기
    this.delegate('click', `.${klass.address_layer} > img`, (_e) => {
      const address_layer = this.element().querySelector(`.${klass.address_layer}`)!;
      (address_layer as HTMLDivElement).style.display = 'none';
    });

    // 이메일 변경 modal
    this.component.change_email_button.addEventListener('click', async (_e) => {
      if (this.data.user.hasPassword) {
        await Modal.open(
          { view: new ChangeEmailModalBodyView({ email: this.data.user.email }) },
          {
            header: {
              title: T('mypage.change_email.title.001'),
            },
            footer: {
              cancel: T('mypage.button.006'),
              confirm: T('mypage.button.011'),
              confirm_option: {
                color: 'primary',
                disabled: true,
              },
            },
            backdrop: true,
            append_to: this.element(),
          },
        );
      } else {
        const res = await makePopup({
          title: T('mypage.popup.title.003'),
          message: T('mypage.popup.message.004'),
          ok: T('mypage.popup.ok.password'),
          cancel: T('common.cancel'),
        });

        if (res === 'confirm') {
          await this.component.set_password_view.openSetPasswordModal();
        }
      }
    });

    // 이메일 변경 완료
    this.addEventListener(ChangeEmailEvent, (e) => {
      const email_input_el = this.element().querySelector('[name="email"]')!;
      const email_input_wrapper_el = email_input_el.closest('.Input')!;
      const email_input_view = rune.getView(email_input_wrapper_el, Input)!;
      const new_email = e.detail.email;
      email_input_view.inputBaseView.setValue(new_email);
      this.data.user.email = new_email;
      this.component.set_password_view.data.email = new_email;

      postUserDataChagedMessage();

      Toast.open({ label: T('mypage.toast.message.001'), close_label: T('common.close') }, {});
    });

    // 비밀번호 변경 완료
    this.addEventListener(ChangePasswordEvent, () => {
      this.data.user.hasPassword = true;
      this.component.set_password_view.data.hasPassword = true;
      this.component.set_password_view.redraw();

      postUserDataChagedMessage();

      Toast.open({ label: T('mypage.toast.message.002'), close_label: T('common.close') }, {});
    });

    // sns 연결 해제 완료 (비밀번호 없는 경우)
    this.addEventListener(SnsDisconnectEvent, (e) => {
      const { email, nickname, sns_type } = e.detail;
      this.data.user.email = email;
      this.data.user.nickname = nickname;

      this.component.email.value = email;
      this.component.nickname.value = nickname;

      Toast.open({ label: T('mypage.toast.message.003'), close_label: T('common.close') }, {});
      this.data.user.hasPassword = this.component.set_password_view.data.hasPassword = true;
      this.component.set_password_view.redraw();
      this.data.user.$many.user_sns_informations = pipe(
        this.data.user.$many.user_sns_informations,
        reject((sns) => sns.sns_type === sns_type),
        toArray,
      );
      this.component.sns_list.data.user = this.data.user;
      this.component.sns_list.redraw();
    });

    // 취소 버튼
    this.component.cancel_button.addEventListener('click', async (_e) => {
      await this.cancel();
    });

    this.addEventListener(Header.HeaderBackEvent, async () => {
      await this.cancel();
    });
  }
}
